import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  CardActionArea,
  Button,
  IconButton,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  WhatsApp as WhatsAppIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import {
  getBlogPostBySlug,
  blogPosts,
  getRecentPosts,
  generateBlogPostStructuredData,
} from '../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Reset states first
    setLoading(true);
    setNotFound(false);
    setPost(null);
    setRelatedPosts([]);
    
    // Small delay to ensure state is set
    const timer = setTimeout(() => {
      const currentPost = getBlogPostBySlug(slug);
      if (currentPost) {
        setPost(currentPost);
        setNotFound(false);
        
        // Get related posts from same category
        const related = blogPosts
          .filter(p => 
            p.id !== currentPost.id && 
            p.category && 
            p.category[language] && 
            currentPost.category && 
            currentPost.category[language] &&
            p.category[language] === currentPost.category[language]
          )
          .slice(0, 3);
        
        // If not enough related posts, add recent posts
        if (related.length < 3) {
          const recentPosts = getRecentPosts(5).filter(p => 
            p.id !== currentPost.id && 
            !related.find(r => r.id === p.id)
          );
          related.push(...recentPosts.slice(0, 3 - related.length));
        }
        
        setRelatedPosts(related);
        
        // Add structured data to head safely
        try {
          const structuredData = generateBlogPostStructuredData(currentPost, language);
          const script = document.createElement('script');
          script.type = 'application/ld+json';
          script.innerHTML = JSON.stringify(structuredData);
          document.head.appendChild(script);
        } catch (error) {
          console.warn('Failed to add structured data:', error);
        }
      } else {
        setNotFound(true);
      }
      
      setLoading(false);
    }, 10);
    
    // Cleanup function
    return () => {
      clearTimeout(timer);
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.parentNode) {
        try {
          document.head.removeChild(existingScript);
        } catch (error) {
          console.warn('Failed to remove structured data script:', error);
        }
      }
    };
  }, [slug, language]);

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {language === 'hi' ? 'लोड हो रहा है...' : 'Loading...'}
        </Typography>
      </Container>
    );
  }

  // Redirect to blog list if post not found
  if (notFound || !post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(post.title[language]);
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(window.location.href);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        return;
      default:
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const createMarkup = (htmlContent) => {
    if (!htmlContent) return { __html: '' };
    return { __html: htmlContent };
  };

  return (
    <>
      <SEOHead
        title={post.title[language]}
        description={post.metaDescription[language]}
        keywords={post.keywords[language]}
        canonical={`https://acharyaabhishekpandey.com/blog/${post.slug}`}
        ogType="article"
        ogTitle={post.title[language]}
        ogDescription={post.excerpt[language]}
        article={{
          publishedTime: post.publishDate,
          modifiedTime: post.publishDate,
          author: post.author[language],
          section: post.category[language],
          tags: post.tags.map(tag => tag[language]),
        }}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <MuiLink component={Link} to="/" color="inherit">
            {language === 'hi' ? 'होम' : 'Home'}
          </MuiLink>
          <MuiLink component={Link} to="/blog" color="inherit">
            {language === 'hi' ? 'ब्लॉग' : 'Blog'}
          </MuiLink>
          <Typography color="text.primary">
            {post.title[language]}
          </Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          component={Link}
          to="/blog"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3 }}
        >
          {language === 'hi' ? 'ब्लॉग पर वापस जाएं' : 'Back to Blog'}
        </Button>

        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              {/* Post Header */}
              <Box sx={{ mb: 4 }}>
                {/* Featured Badge */}
                {post.featured && (
                  <Chip
                    label={language === 'hi' ? 'फीचर्ड' : 'Featured'}
                    color="warning"
                    sx={{ mb: 2 }}
                  />
                )}

                {/* Title */}
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                    lineHeight: 1.2,
                    mb: 3,
                  }}
                >
                  {post.title[language]}
                </Typography>

                {/* Post Meta */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <PersonIcon sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2">
                      {post.author[language]}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <CalendarIcon sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2">
                      {formatDate(post.publishDate)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                    <ScheduleIcon sx={{ fontSize: 18, mr: 1 }} />
                    <Typography variant="body2">
                      {post.readTime[language]}
                    </Typography>
                  </Box>
                </Box>

                {/* Category and Tags */}
                <Box sx={{ mb: 3 }}>
                  <Chip
                    label={post.category[language]}
                    color="primary"
                    sx={{ mr: 1, mb: 1 }}
                  />
                  {post.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag[language]}
                      variant="outlined"
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>

                {/* Share Buttons */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                    {language === 'hi' ? 'शेयर करें:' : 'Share:'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleShare('facebook')}
                    sx={{ color: '#1877F2' }}
                  >
                    <FacebookIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleShare('twitter')}
                    sx={{ color: '#1DA1F2' }}
                  >
                    <TwitterIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleShare('whatsapp')}
                    sx={{ color: '#25D366' }}
                  >
                    <WhatsAppIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleShare('copy')}
                    sx={{ color: 'text.secondary' }}
                  >
                    <LinkIcon />
                  </IconButton>
                </Box>

                {/* Copy Success Alert */}
                {copySuccess && (
                  <Alert severity="success" sx={{ mt: 2, mb: 0 }}>
                    {language === 'hi' ? 'लिंक कॉपी हो गया!' : 'Link copied to clipboard!'}
                  </Alert>
                )}
              </Box>

              <Divider sx={{ mb: 4 }} />

              {/* Post Content */}
              <Box
                sx={{
                  '& h2': {
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '1.5rem',
                    mt: 3,
                    mb: 2,
                  },
                  '& h3': {
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    mt: 2,
                    mb: 1.5,
                  },
                  '& p': {
                    lineHeight: 1.8,
                    mb: 2,
                    fontSize: '1rem',
                    color: 'text.primary',
                  },
                  '& ul, & ol': {
                    pl: 3,
                    mb: 2,
                  },
                  '& li': {
                    mb: 1,
                    lineHeight: 1.6,
                  },
                  '& strong': {
                    fontWeight: 600,
                    color: 'primary.main',
                  },
                }}
                dangerouslySetInnerHTML={createMarkup(post.content[language])}
              />

              {/* Ad Placement Area - Middle of Post */}
              <Paper
                elevation={1}
                sx={{
                  p: 3,
                  my: 4,
                  textAlign: 'center',
                  backgroundColor: 'grey.50',
                  minHeight: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    {language === 'hi' ? 'विज्ञापन स्थान' : 'Advertisement Space'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {language === 'hi' 
                      ? 'यहां आपका Google AdSense विज्ञापन दिखेगा'
                      : 'Your Google AdSense ad will appear here'
                    }
                  </Typography>
                </Box>
              </Paper>

              {/* Author Box */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 3, 
                  mt: 4, 
                  backgroundColor: 'primary.light', 
                  color: 'white',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {post.author[language]}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {language === 'hi' 
                        ? 'प्रामाणिक ज्योतिष और तंत्र विशेषज्ञ'
                        : 'Authentic Astrology & Tantra Expert'
                      }
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {language === 'hi'
                    ? '15+ वर्षों के अनुभव के साथ आचार्य अभिषेक पांडेय जी ज्योतिष, तंत्र-मंत्र और रत्न विज्ञान के क्षेत्र में प्रसिद्ध हैं।'
                    : 'With 15+ years of experience, Acharya Abhishek Pandey ji is renowned in the field of astrology, tantra-mantra and gemstone science.'
                  }
                </Typography>
              </Paper>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              {/* Ad Placement Area - Sidebar Top */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 4,
                  textAlign: 'center',
                  backgroundColor: 'grey.50',
                  minHeight: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    {language === 'hi' ? 'साइडबार विज्ञापन' : 'Sidebar Advertisement'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    300x250 px Google AdSense
                  </Typography>
                </Box>
              </Paper>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    {language === 'hi' ? 'संबंधित पोस्ट्स' : 'Related Posts'}
                  </Typography>
                  {relatedPosts.map((relatedPost) => (
                    <Card 
                      key={relatedPost.id} 
                      elevation={1}
                      sx={{ 
                        mb: 2, 
                        '&:last-child': { mb: 0 },
                        '&:hover': { boxShadow: 3 },
                      }}
                    >
                      <CardActionArea component={Link} to={`/blog/${relatedPost.slug}`}>
                        <CardContent sx={{ py: 2 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              lineHeight: 1.4,
                              mb: 1,
                            }}
                          >
                            {relatedPost.title[language]}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Chip
                              label={relatedPost.category[language]}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {relatedPost.readTime[language]}
                            </Typography>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Paper>
              )}

              {/* Contact CTA */}
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  textAlign: 'center', 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {language === 'hi' ? 'व्यक्तिगत परामर्श चाहिए?' : 'Need Personal Consultation?'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                  {language === 'hi'
                    ? 'अपनी समस्या का समाधान पाने के लिए आज ही संपर्क करें'
                    : 'Contact today to get solution to your problems'
                  }
                </Typography>
                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: 'white',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'grey.100',
                    },
                  }}
                >
                  {language === 'hi' ? 'संपर्क करें' : 'Contact Now'}
                </Button>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default BlogPost;